import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import cmtService from './cmtService';

/**
 * 🚨 Enhanced Incident Service for Multi-CMT Support
 * 
 * Provides backward-compatible incident operations with CMT filtering support.
 * Automatically handles legacy single-CMT organizations and new multi-CMT setups.
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - Phase 1 Multi-CMT Implementation
 */

// Cache for performance optimization
const incidentCache = new Map();
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes for incidents (fresher data)

export const incidentService = {

  /**
   * 📊 Subscribe to current incidents for dashboard display
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID for filtering (optional)
   * @param {Object} options - Options including includeChildren
   * @param {function} callback - Callback function to receive updates
   * @param {function} onError - Error callback (optional)
   * @returns {function} Unsubscribe function
   */
  async subscribeCurrentIncidents(orgId, cmtId = null, options = {}, callback, onError = null) {
    try {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      console.log('🚨 Setting up current incidents subscription for org:', orgId, 'CMT:', cmtId);

      // Determine CMT scope (similar to getIncidents logic)
      const includeChildren = options.includeChildren !== false; // default true
      let targetCmtIds = [];

      if (cmtId) {
        targetCmtIds = [cmtId];
        if (includeChildren) {
          try {
            console.log('🚨 Dashboard: Fetching children for CMT:', cmtId);
            const { default: cmtService } = await import('./cmtService');
            const directChildren = await cmtService.getChildCMTs(orgId, cmtId);
            console.log('🚨 Dashboard: Found children:', directChildren);
            if (Array.isArray(directChildren) && directChildren.length > 0) {
              targetCmtIds = [cmtId, ...directChildren.map(c => c.id)].filter(Boolean);
              console.log('🚨 Dashboard: Final targetCmtIds:', targetCmtIds);
            }
          } catch (e) {
            console.warn('Dashboard: failed to load direct children, falling back to single CMT scope', e);
          }
        }
      }

      // Build query with CMT filtering
      const incidentsRef = collection(db, 'c3_incidents');
      const queryConstraints = [
        where('orgId', '==', orgId),
        where('status', 'in', ['Active', 'Exercise']),
        orderBy('updatedAt', 'desc')
      ];

      // Add CMT filtering if specified
      if (targetCmtIds.length === 1) {
        queryConstraints.splice(2, 0, where('cmtId', '==', targetCmtIds[0]));
      } else if (targetCmtIds.length > 1) {
        queryConstraints.splice(2, 0, where('cmtId', 'in', targetCmtIds));
      }

      const q = query(incidentsRef, ...queryConstraints);

      const unsubscribe = onSnapshot(q, (snapshot) => {
        try {
          const incidents = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          console.log('🚨 Current incidents update:', incidents.length, 'incidents');
          console.log('🚨 Raw incidents data:', incidents);

          // Group incidents by categories for pill display
          const counts = {
            critical: 0,
            medical: 0,
            suspicious: 0,
            total: incidents.length
          };

          incidents.forEach(incident => {
            console.log('🚨 Processing incident:', {
              id: incident.id,
              title: incident.title,
              severity: incident.severity,
              crisisType: incident.crisisType,
              type: incident.type,
              status: incident.status
            });

            // Categorize by actual TYPE/CRISIS TYPE to show real data cards
            const incidentType = incident.crisisType || incident.type || 'Unknown';
            
            // Add to type-specific counts (create dynamic categories)
            if (!counts.byType) counts.byType = {};
            if (!counts.byType[incidentType]) counts.byType[incidentType] = 0;
            counts.byType[incidentType]++;
            
            console.log('🚨 Found incident:', {
              title: incident.title,
              type: incidentType,
              severity: incident.severity,
              status: incident.status
            });
          });

          console.log('🚨 Final counts:', counts);

          // Get latest 3 incidents for preview
          const latest = incidents.slice(0, 3).map(incident => ({
            id: incident.id,
            title: incident.title || incident.incidentTitle || 'Untitled Incident',
            type: incident.crisisType || incident.type || 'Unknown',
            severity: incident.severity || 'Unknown',
            status: incident.status,
            updatedAt: incident.updatedAt
          }));

          const result = {
            total: counts.total,
            byCategory: {
              critical: counts.critical,
              medical: counts.medical,
              suspicious: counts.suspicious
            },
            byType: counts.byType || {},
            latest,
            incidents // Full incident list for navigation
          };

          console.log('🚨 Current incidents result:', result);
          callback(result);

        } catch (error) {
          console.error('Error processing current incidents snapshot:', error);
          if (onError) onError(error);
        }
      }, (error) => {
        console.error('Error in current incidents subscription:', error);
        if (onError) onError(error);
      });

      return unsubscribe;

    } catch (error) {
      console.error('Error setting up current incidents subscription:', error);
      if (onError) onError(error);
      return () => {}; // Return no-op unsubscribe function
    }
  },

  /**
   * 📋 Get incidents with CMT filtering support and hierarchy awareness
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID (optional, falls back to default)
   * @param {Object} options - Query options + hierarchy options
   * @returns {Promise<Array>} Array of incident objects
   */
  async getIncidents(orgId, cmtId = null, options = {}) {
    try {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      // Include includeChildren in cache key to prevent wrong cached results
      const includeChildrenParam = options.includeChildren !== false;
      const cacheKey = `incidents:${orgId}:${cmtId || 'default'}:children_${includeChildrenParam}`;
      const cached = this._getFromCache(cacheKey);
      if (cached && !options.skipCache) {
        console.log('🚨 DEBUGGING: Returning cached result for:', cacheKey);
        return cached;
      }

      // Determine query scope based on one-hop children visibility
      // New behavior: For ANY CMT, optionally include direct children (one level only)
      const includeChildren = options.includeChildren !== false; // default true
      let queryScope = 'single'; // 'single' or 'onehop'
      let targetCmtIds = [];

      console.log('🚨 DEBUGGING TOGGLE:', {
        'options.includeChildren': options.includeChildren,
        'includeChildren': includeChildren,
        'cmtId': cmtId,
        'orgId': orgId
      });

      if (cmtId) {
        targetCmtIds = [cmtId];
        console.log('🚨 DEBUGGING: About to check includeChildren condition, includeChildren =', includeChildren);
        if (includeChildren) {
          console.log('🚨 DEBUGGING: Inside includeChildren block');
          try {
            console.log('🔍 TOGGLE DEBUG: Fetching children for CMT:', cmtId);
            const directChildren = await cmtService.getChildCMTs(orgId, cmtId);
            console.log('🔍 TOGGLE DEBUG: Found children:', directChildren);
            if (Array.isArray(directChildren) && directChildren.length > 0) {
              targetCmtIds = [cmtId, ...directChildren.map(c => c.id)].filter(Boolean);
              queryScope = 'onehop';
              console.log('🔍 TOGGLE DEBUG: Final targetCmtIds:', targetCmtIds);
            } else {
              console.log('🔍 TOGGLE DEBUG: No children found or empty array');
            }
          } catch (e) {
            // Safe fallback: ignore children on error
            console.warn('getIncidents: failed to load direct children, falling back to single scope', e);
          }
        } else {
          console.log('🔍 TOGGLE DEBUG: includeChildren is false, showing only:', cmtId);
        }
      }

      // Build query with CMT awareness
      const incidentsRef = collection(db, 'c3_incidents');
      const queryConstraints = [
        where('orgId', '==', orgId),
        where('status', '!=', 'deleted'),
        orderBy('status'), // Required for != operator
        orderBy('createdAt', 'desc')
      ];

      // Add CMT filtering based on scope
      if (targetCmtIds.length === 1) {
        queryConstraints.splice(2, 0, where('cmtId', '==', targetCmtIds[0]));
      } else if (targetCmtIds.length > 1) {
        queryConstraints.splice(2, 0, where('cmtId', 'in', targetCmtIds));
      }

      const q = query(incidentsRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);
      
      let incidents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));

      // 🔄 BACKWARD COMPATIBILITY: Handle incidents without cmtId
      if (cmtId && incidents.length === 0 && queryScope === 'single') {
        console.log('🔄 No CMT-specific incidents found, checking for legacy incidents...');
        
        // Query incidents without cmtId (legacy incidents)
        const legacyQuery = query(
          incidentsRef,
          where('orgId', '==', orgId),
          where('status', '!=', 'deleted'),
          orderBy('status'),
          orderBy('createdAt', 'desc')
        );
        
        const legacySnapshot = await getDocs(legacyQuery);
        const legacyIncidents = legacySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(incident => !incident.cmtId); // Only incidents without cmtId
        
        if (legacyIncidents.length > 0) {
          console.log(`🔄 Found ${legacyIncidents.length} legacy incidents, auto-assigning to CMT ${cmtId}`);
          
          // Auto-assign legacy incidents to the specified CMT
          await this._assignLegacyIncidentsToCMT(legacyIncidents, cmtId);
          
          // Include them in the result
          incidents = [...incidents, ...legacyIncidents.map(incident => ({
            ...incident,
            cmtId: cmtId, // Add cmtId for UI consistency
            createdAt: incident.createdAt?.toDate(),
            updatedAt: incident.updatedAt?.toDate()
          }))];
        }
      }

      // Apply additional filters
      if (options.status) {
        incidents = incidents.filter(incident => incident.status === options.status);
      }

      if (options.priority) {
        incidents = incidents.filter(incident => incident.priority === options.priority);
      }

      if (options.limit) {
        incidents = incidents.slice(0, options.limit);
      }

      // Logging for one-hop scope debugging
      console.log(`📊 Incident query completed:`, {
        orgId,
        cmtId,
        queryScope,
        targetCmtIds: targetCmtIds.length > 0 ? targetCmtIds : ['all'],
        incidentCount: incidents.length,
        oneHop: queryScope === 'onehop',
        includeChildren: includeChildren,
        cacheKey: cacheKey
      });

      this._setCache(cacheKey, incidents);
      console.log('🚨 DEBUGGING: Cached results for key:', cacheKey, 'incidents:', incidents.length);
      return incidents;

    } catch (error) {
      console.error('Error getting incidents:', error);
      throw new Error(`Failed to get incidents: ${error.message}`);
    }
  },

  /**
   * 🌍 Get hierarchical incidents for Global CMT
   * Specifically designed for Global CMT users to see all incidents across child CMTs
   * @param {string} orgId - Organization ID
   * @param {string} globalCmtId - Global CMT ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of incidents from Global and all child CMTs
   */
  async getHierarchicalIncidents(orgId, globalCmtId, options = {}) {
    try {
      if (!orgId || !globalCmtId) {
        throw new Error('Organization ID and Global CMT ID are required');
      }

      // Verify this is actually a Global CMT
      const globalCmt = await cmtService.getCMT(orgId, globalCmtId);
      if (!globalCmt.isGlobalCmt) {
        console.warn('⚠️ Non-global CMT requested hierarchical view, falling back to standard query');
        return this.getIncidents(orgId, globalCmtId, options);
      }

      // Get all incidents with hierarchy enabled
      return this.getIncidents(orgId, globalCmtId, {
        ...options,
        enableHierarchy: true
      });

    } catch (error) {
      console.error('Error getting hierarchical incidents:', error);
      throw new Error(`Failed to get hierarchical incidents: ${error.message}`);
    }
  },

  /**
   * 📡 Set up real-time incident listener with CMT filtering
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID (optional)
   * @param {Function} callback - Callback function for updates
   * @param {Function} errorCallback - Error callback function
   * @returns {Function} Unsubscribe function
   */
  setupIncidentListener(orgId, cmtId = null, callback, errorCallback) {
    try {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      console.log(`📡 Setting up incident listener for org:${orgId}, cmt:${cmtId || 'all'}`);

      const incidentsRef = collection(db, 'c3_incidents');
      const queryConstraints = [
        where('orgId', '==', orgId),
        where('status', '!=', 'deleted'),
        orderBy('status'), // Required for != operator
        orderBy('createdAt', 'desc')
      ];

      // Add CMT filtering if CMT ID is provided
      if (cmtId) {
        queryConstraints.splice(2, 0, where('cmtId', '==', cmtId));
      }

      const q = query(incidentsRef, ...queryConstraints);

      return onSnapshot(q, 
        (querySnapshot) => {
          console.log(`📡 Incident update received: ${querySnapshot.size} documents`);
          
          const incidents = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate()
          }));

          // Clear cache for this query
          const cacheKey = `incidents:${orgId}:${cmtId || 'default'}`;
          this._clearCache(cacheKey);

          callback(incidents);
        },
        (error) => {
          console.error('📡 Incident listener error:', error);
          if (errorCallback) {
            errorCallback(error);
          }
        }
      );

    } catch (error) {
      console.error('Error setting up incident listener:', error);
      if (errorCallback) {
        errorCallback(error);
      }
      return () => {}; // Return no-op unsubscribe function
    }
  },

  /**
   * ➕ Create new incident with CMT assignment
   * @param {Object} incidentData - Incident data
   * @param {string} cmtId - CMT ID to assign incident to
   * @returns {Promise<Object>} Created incident object
   */
  async createIncident(incidentData, cmtId) {
    try {
      if (!incidentData.orgId) {
        throw new Error('Organization ID is required');
      }

      if (!cmtId) {
        // Try to get default CMT if none specified
        const defaultCmt = await cmtService.getDefaultCMT(incidentData.orgId);
        if (defaultCmt) {
          cmtId = defaultCmt.id;
          console.log(`🎯 Auto-assigned incident to default CMT: ${defaultCmt.name}`);
        }
      }

      const incidentToCreate = {
        ...incidentData,
        cmtId: cmtId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Ensure required fields
        status: incidentData.status || 'active',
        priority: incidentData.priority || 'medium'
      };

      const docRef = await addDoc(collection(db, 'c3_incidents'), incidentToCreate);

      // Clear cache
      this._clearCacheForOrg(incidentData.orgId);

      return {
        id: docRef.id,
        ...incidentToCreate,
        createdAt: new Date(),
        updatedAt: new Date()
      };

    } catch (error) {
      console.error('Error creating incident:', error);
      throw new Error(`Failed to create incident: ${error.message}`);
    }
  },

  /**
   * 🔄 Update existing incident
   * @param {string} incidentId - Incident ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated incident object
   */
  async updateIncident(incidentId, updates) {
    try {
      if (!incidentId) {
        throw new Error('Incident ID is required');
      }

      const docRef = doc(db, 'c3_incidents', incidentId);
      
      // Validate incident exists
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('Incident not found');
      }

      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: updates.updatedBy
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await updateDoc(docRef, updateData);

      // Clear cache
      const orgId = docSnap.data().orgId;
      if (orgId) {
        this._clearCacheForOrg(orgId);
      }

      return this.getIncident(incidentId);

    } catch (error) {
      console.error('Error updating incident:', error);
      throw new Error(`Failed to update incident: ${error.message}`);
    }
  },

  /**
   * 🆔 Get specific incident by ID
   * @param {string} incidentId - Incident ID
   * @returns {Promise<Object>} Incident object
   */
  async getIncident(incidentId) {
    try {
      if (!incidentId) {
        throw new Error('Incident ID is required');
      }

      const docRef = doc(db, 'c3_incidents', incidentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Incident not found');
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate(),
        updatedAt: docSnap.data().updatedAt?.toDate()
      };

    } catch (error) {
      console.error('Error getting incident:', error);
      throw new Error(`Failed to get incident: ${error.message}`);
    }
  },

  /**
   * 🗑️ Delete incident (soft delete by setting status to deleted)
   * @param {string} incidentId - Incident ID
   * @param {string} deletedBy - User ID performing deletion
   * @returns {Promise<boolean>} Success status
   */
  async deleteIncident(incidentId, deletedBy) {
    try {
      if (!incidentId) {
        throw new Error('Incident ID is required');
      }

      await this.updateIncident(incidentId, {
        status: 'deleted',
        deletedAt: new Date(),
        deletedBy: deletedBy,
        updatedBy: deletedBy
      });

      return true;

    } catch (error) {
      console.error('Error deleting incident:', error);
      throw new Error(`Failed to delete incident: ${error.message}`);
    }
  },

  /**
   * 🔄 Transfer incident to different CMT
   * @param {string} incidentId - Incident ID
   * @param {string} targetCmtId - Target CMT ID
   * @param {string} transferredBy - User ID performing transfer
   * @returns {Promise<Object>} Updated incident object
   */
  async transferIncidentToCMT(incidentId, targetCmtId, transferredBy) {
    try {
      if (!incidentId || !targetCmtId) {
        throw new Error('Incident ID and target CMT ID are required');
      }

      const incident = await this.getIncident(incidentId);
      
      // Verify target CMT exists
      const targetCmt = await cmtService.getCMT(incident.orgId, targetCmtId);
      if (!targetCmt) {
        throw new Error('Target CMT not found');
      }

      return this.updateIncident(incidentId, {
        cmtId: targetCmtId,
        transferHistory: [
          ...(incident.transferHistory || []),
          {
            fromCmtId: incident.cmtId,
            toCmtId: targetCmtId,
            transferredBy: transferredBy,
            transferredAt: new Date(),
            reason: 'Manual transfer'
          }
        ],
        updatedBy: transferredBy
      });

    } catch (error) {
      console.error('Error transferring incident:', error);
      throw new Error(`Failed to transfer incident: ${error.message}`);
    }
  },

  // ==========================================
  // 🔧 PRIVATE HELPER METHODS
  // ==========================================

  /**
   * 🔄 Auto-assign legacy incidents to CMT
   * @private
   */
  async _assignLegacyIncidentsToCMT(incidents, cmtId) {
    try {
      if (incidents.length === 0) return;

      console.log(`🔄 Auto-assigning ${incidents.length} legacy incidents to CMT ${cmtId}`);

      const batch = writeBatch(db);
      
      incidents.forEach(incident => {
        const docRef = doc(db, 'c3_incidents', incident.id);
        batch.update(docRef, {
          cmtId: cmtId,
          migrationUpdatedAt: serverTimestamp(),
          migrationNote: 'Auto-assigned to CMT during multi-CMT migration'
        });
      });

      await batch.commit();
      console.log(`✅ Successfully assigned ${incidents.length} incidents to CMT ${cmtId}`);

    } catch (error) {
      console.error('❌ Failed to assign legacy incidents:', error);
      // Don't throw - this is a background operation
    }
  },

  /**
   * 💾 Cache management
   * @private
   */
  _getFromCache(key) {
    const cached = incidentCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
    return null;
  },

  _setCache(key, data) {
    incidentCache.set(key, {
      data,
      timestamp: Date.now()
    });
  },

  _clearCache(key) {
    incidentCache.delete(key);
  },

  _clearCacheForOrg(orgId) {
    const keysToDelete = [];
    for (const key of incidentCache.keys()) {
      if (key.includes(orgId)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => incidentCache.delete(key));
  }
};

export default incidentService;